import { useTranslation } from "react-i18next";
import { X } from "@phosphor-icons/react";
import { Button } from "@libs/zapi-ui/ds/components/button";
import { WidgetCardBody } from "@libs/zapi-ui/ds/components/widget-card";
import type { TRqaSimWidget } from "@/zapi-frontend/shared/types/rqaSim";
import { applyRefs, type TSpelledParts, type TTwinValues } from "@/zapi-frontend/shared/lib/rqaSim/twin";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { renderBlock } from "../MemoIsland/ChapterBlocks";
import { WidgetCard, WidgetCardHeader } from "../ProjectLayout/ui/WidgetCard";
import { TwinBox } from "./_TwinBox";

type TTwinPinnedBandProps = {
  widgets: TRqaSimWidget[];
  valueOf: (key: string) => TSpelledParts | undefined;
  labelOf: (key: string) => string;
  twinValues: TTwinValues;
  isHeld: (key: string) => boolean;
  hasNoReturn: (key: string) => boolean;
  wasOf: (key: string) => string | undefined;
  onUnpin: (widget: TRqaSimWidget) => void;
};

const spansOf = (widgets: TRqaSimWidget[]): number[] => {
  const spans = widgets.map(() => 1);
  let waiting = -1;
  widgets.forEach((widget, index) => {
    if (widget.isWide) {
      if (waiting >= 0) spans[waiting] = 2;
      waiting = -1;
      spans[index] = 2;
    } else if (waiting >= 0) {
      waiting = -1;
    } else {
      waiting = index;
    }
  });
  if (waiting >= 0) spans[waiting] = 2;
  return spans;
};

export function TwinPinnedBand({
  widgets,
  valueOf,
  labelOf,
  twinValues,
  isHeld,
  hasNoReturn,
  wasOf,
  onUnpin
}: TTwinPinnedBandProps) {
  const { t } = useTranslation("projectHome");
  if (widgets.length === 0) return null;
  const spans = spansOf(widgets);

  return (
    <div className="grid gap-4 @2xl:grid-cols-2" data-testid="rqa-sim-pinned">
      {widgets.map((widget, index) => {
        const id = widget.key ?? String(widget.id);
        const block = widget.kind === "block" && widget.block ? applyRefs([widget.block], twinValues)[0] : null;
        const blockLabel = block && "label" in block ? block.label : null;
        const bareBlock = block && "label" in block ? { ...block, label: null } : block;
        const title = widget.key ? labelOf(widget.key) : blockLabel || widget.prompt;
        return (
          <WidgetCard
            key={widget.id}
            className={cn(spans[index] === 2 && "@2xl:col-span-2")}
            data-testid={`rqa-sim-pinned-${id}`}
            data-wide={spans[index] === 2}
          >
            <WidgetCardHeader
              title={title}
              titleOverflow="clamp"
              actions={
                <Button
                  variant="ghost"
                  size="icon"
                  className="rounded-full text-muted-foreground"
                  aria-label={t("rqaSim.unpin")}
                  title={t("rqaSim.unpin")}
                  data-testid={`rqa-sim-unpin-${id}`}
                  onClick={() => onUnpin(widget)}
                >
                  <X />
                </Button>
              }
            />
            <WidgetCardBody className="min-w-0 gap-3">
              {bareBlock ? (
                <div className="min-w-0 [&_figure.border]:bg-card">{renderBlock(bareBlock, 0)}</div>
              ) : widget.key ? (
                <TwinBox
                  size="card"
                  figureKey={widget.key}
                  label={labelOf(widget.key)}
                  value={valueOf(widget.key)}
                  stale={isHeld(widget.key)}
                  noReturn={hasNoReturn(widget.key)}
                  was={wasOf(widget.key)}
                />
              ) : null}
              {widget.createdByName && (
                <span className="typo-caption text-muted-foreground">
                  {t("rqaSim.pinnedBy", { name: widget.createdByName })}
                </span>
              )}
            </WidgetCardBody>
          </WidgetCard>
        );
      })}
    </div>
  );
}
